import React, { FunctionComponent, useEffect, useLayoutEffect, useRef, useState } from 'react'
import axios from 'axios'
import { Link, Navigate, Route, Routes, useNavigate } from 'react-router-dom'
import { HostData } from '../Customer/Data'
import { HealthCheckBrief } from './HealthCheckBrief'
import { FleetMakeupBrief } from './FleetMakeupBrief'
import { SecurityBrief } from './SecurityBrief'
import { SecurityDemoBrief } from './SecurityDemoBrief' /* DEMO ONLY */
import {
  BOARD_COLUMNS,
  BoardColumn,
  isLifecycleStage,
  lifecycleStageFor,
  LifecycleStage,
  stageOrDefault,
} from './lifecycle'
import styles from './BriefsHub.module.css'
import { useDemoOptions } from './DemoOptionsPanel' /* DEMO ONLY */
import tableStyles from './CustomerIntelligenceTable.module.css'
import { BriefThumb } from './BriefThumb'
import { ReactComponent as HealthCheckArt } from '../../assets/img/brief-thumb-health-check.svg' /* DEMO ONLY */
import { ReactComponent as FleetMakeupArt } from '../../assets/img/brief-thumb-fleet-makeup.svg' /* DEMO ONLY */
import { ReactComponent as SecurityArt } from '../../assets/img/brief-thumb-security.svg' /* DEMO ONLY */

interface Props {
  teamID: string
  host: HostData
}

type ThumbBrief = React.ComponentProps<typeof BriefThumb>['brief']

/* a brief on the board: one that can be opened (with its link and thumbnail), or one that's coming soon */
interface BoardBrief {
  title: string
  /** where it opens, and the brief it is (its thumbnail and data-brief): absent while it's coming soon */
  open?: { to: string; brief: string; thumb: ThumbBrief }
  /** just made available: flagged New */
  isNew?: boolean
}

/* a wallet: the briefs for one job, in one lifecycle column (stacked back to front, in this order) */
interface Wallet {
  column: BoardColumn
  name: string
  briefs: BoardBrief[]
}

const HEALTH_CHECK = { to: 'health-check', brief: 'Health check', thumb: 'health-check' as const }
const SECURITY = { to: 'security', brief: 'Security', thumb: 'security' as const }

const WALLETS: Wallet[] = [
  {
    column: 'trial',
    name: 'Proposal & planning',
    briefs: [
      { title: 'Industry benchmark' },
      // DEMO ONLY: a mock of the Security brief, made-up data (SecurityDemoBrief)
      { title: 'Security (DEMO)', open: { to: 'security-demo', brief: 'Security (DEMO)', thumb: 'security' } },
      { title: 'Volume forecasting/trends' },
    ],
  },
  {
    column: 'onboarding',
    name: 'Feature usage',
    briefs: [{ title: 'Feature utilization' }, { title: 'Environment, social & governance' }],
  },
  {
    column: 'onboarding',
    name: 'System usage',
    briefs: [
      { title: 'System stability' },
      { title: 'Business review' },
      { title: 'Printer security checklist', open: SECURITY },
    ],
  },
  {
    column: 'serve',
    name: 'Activity overview',
    briefs: [{ title: 'Business review' }, { title: 'Feature utilization' }, { title: 'Volume forecasting/trends' }],
  },
  {
    column: 'serve',
    name: 'System health',
    briefs: [
      { title: 'Print security checkup', open: SECURITY },
      { title: 'Health check', open: HEALTH_CHECK, isNew: true },
    ],
  },
  {
    column: 'upsell',
    name: 'Opportunities',
    briefs: [
      { title: 'Fleet makeup', open: { to: 'fleet-makeup', brief: 'Fleet makeup', thumb: 'fleet-makeup' } },
      { title: 'Additional embeddeds & licenses' },
      { title: 'License & feature expansion' },
      { title: 'Benchmark & compare' },
      { title: 'Business review' },
      { title: 'Volume forecasting/trends' },
    ],
  },
  {
    column: 'retention',
    name: 'Renewal maximizer',
    briefs: [{ title: 'Business review' }, { title: 'Health check', open: HEALTH_CHECK }],
  },
  { column: 'expired', name: 'Win back', briefs: [{ title: 'Win-back proposal' }] },
]

/* a brief's title, flagged New when it's just been made available (in place of a tip over the board) */
const BriefTitle: FunctionComponent<{ brief: BoardBrief }> = ({ brief }) => (
  <span>
    {brief.title}
    {brief.isNew && <span className={styles.newBadge}>New</span>}
  </span>
)

/* DEMO ONLY: an item's place in its view's entrance (rows, then books, one after another), as --i */
const entranceOrder = (i: number) => (el: HTMLElement | null) => el?.style.setProperty('--i', String(i))

/* one wallet of briefs. With coming-soon briefs folded away (DEMO ONLY: a display preference), they become one card
   at the back of the wallet, "+N coming soon", which opens them out */
/* the brief's artwork drawn inline on its cover, so CSS can recolour it in the cover's own tones, as the Info
   Center's documents have theirs */
const BOOK_ART: Record<ThumbBrief, FunctionComponent<React.SVGProps<SVGSVGElement>>> = {
  'health-check': HealthCheckArt,
  'fleet-makeup': FleetMakeupArt,
  security: SecurityArt,
}

/* the stage's colour in a few shades, from its darkest: the covers along a shelf take them in turn, as do its
   spines (starting a shade lighter, so a cover stands out from the spines beside it) */
const COVER_SHADES = 4
/* DEMO ONLY: a wallet can be opened, from its pocket, to fan its briefs out sideways over the board:
   its stacked cards rise out of the pocket and morph into a hand of A4 pages, each its artwork with its title over
   it; one wallet at a time */
const FAN_SPREAD_REM = 36 /* the widest the fan's cards spread, first to last, centre to centre */
const FAN_STEP_REM = 7.5 /* between a ready brief and its neighbours (a card is 9rem wide, so its title shows) */
const FAN_PACKED_REM = 2.75 /* between coming-soon pages, packed close, as a pile */
const FAN_CARD_REM = 9
const FAN_CLOSE_MS = 380

interface CardBox {
  x: number
  y: number
  w: number
  h: number
  r: number
  /** in the fan, how far the cards after it slide along to show it, when it's hovered */
  part?: number
}

/* where each card stands in the stack, by its brief's title, in px from the wallet's top left (a brief folded away
   stands where the folded "+N" card does) */
const stackBoxes = (wallet: HTMLElement): Map<string, CardBox> => {
  const origin = wallet.getBoundingClientRect()
  const boxes = new Map<string, CardBox>()
  wallet.querySelectorAll<HTMLElement>('[data-list="briefings"] > ul > li').forEach(card => {
    const box = card.getBoundingClientRect()
    boxes.set(card.dataset.title ?? '', {
      x: box.left - origin.left,
      y: box.top - origin.top,
      w: box.width,
      h: Math.min(box.height, 11 * 16),
      r: 0,
    })
  })
  return boxes
}

/* where each card goes in the fan: A4 pages across, the ready briefs given room and the coming-soon ones packed close
   (a hovered page parts the hand to show itself); centred on the wallet (moved in just enough to stay on the board
   for a wallet at either end), a touch lower towards the ends along a shallow arc and turned slightly out, held a
   little above the pocket */
const fanBoxes = (wallet: HTMLElement, ready: boolean[]): CardBox[] => {
  const rem = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16
  const w = FAN_CARD_REM * rem
  const h = w * 1.414
  // each card's place across, from the first: a full step beside a ready brief, a packed one between coming-soon pages
  const steps = ready.map((isReady, i) =>
    i === 0 ? 0 : (isReady || ready[i - 1] ? FAN_STEP_REM : FAN_PACKED_REM) * rem
  )
  const across = steps.reduce<number[]>((list, step, i) => [...list, (list[i - 1] ?? 0) + step], [])
  const span = across[across.length - 1] ?? 0
  const scale = span > FAN_SPREAD_REM * rem ? (FAN_SPREAD_REM * rem) / span : 1
  const box = wallet.getBoundingClientRect()
  const board = wallet.closest('[data-group="process"]')?.getBoundingClientRect()
  let centre = box.width / 2
  if (board) {
    const half = (span * scale) / 2 + w / 2 + 12 // and a little for the turn
    const middle = box.left + centre
    centre += Math.max(board.left - (middle - half), 0) + Math.min(board.right - (middle + half), 0)
  }
  const foot = box.height - 4.75 * rem // clear of the pocket (3.5rem), a little above it
  return across.map((at, i) => {
    const offset = ((at - span / 2) * scale) / (FAN_STEP_REM * rem) // in full steps from the middle
    // hovered, it's shown all but its last edge: the next card slides along only as far as that takes (no further,
    // so the hand never opens a gap)
    const next = i < across.length - 1 ? (across[i + 1] - at) * scale : w
    return {
      part: Math.max(0, w * 0.88 - next),
      x: centre + offset * FAN_STEP_REM * rem - w / 2,
      y: foot - h + offset * offset * 0.15 * rem,
      w,
      h,
      r: Math.max(-10, Math.min(10, offset * 3.5)),
    }
  })
}

/* a card's box (and turn), and its turn in the cards' movement */
const placeCard = (box: CardBox, i: number) => (el: HTMLElement | null) => {
  if (!el) return
  el.style.setProperty('--fx', `${box.x}px`)
  el.style.setProperty('--fy', `${box.y}px`)
  el.style.setProperty('--fw', `${box.w}px`)
  el.style.setProperty('--fh', `${box.h}px`)
  el.style.setProperty('--fr', `${box.r}deg`)
  el.style.setProperty('--fi', String(i))
  el.style.setProperty('--fpart', `${box.part ?? 0}px`)
}

type FanStage = 'from' | 'open' | 'closing' | 'landing'

/* the wallet's briefs as they leave the stack and fan out: each starts as its stacked card, exactly where that card
   stands (the stack hiding behind them), and moves, reshapes and turns into an A4 page in the same style. Closing,
   each goes back to its card in the stack. The ready briefs stand in front of the coming-soon ones. */
const FanCards: FunctionComponent<{
  wallet: Wallet
  stage: FanStage
  from: Map<string, CardBox>
  to: CardBox[]
}> = ({ wallet, stage, from, to }) => (
  <div className={styles.fanLayer} data-stage={stage}>
    {wallet.briefs.map((brief, i) => {
      const box = stage === 'open' ? to[i] : from.get(brief.title) ?? from.get('') ?? to[i]
      // a coming-soon card's artwork as in the stack: the two briefs' in turn
      const art = brief.open?.thumb ?? (i % 2 === 1 ? 'fleet-makeup' : 'health-check')
      const faces = (
        // the card as it is in the stack: its artwork filling it and its title over the artwork, top left (the title
        // fading out as the card moves and reshapes, and back in once it's in place)
        <span className={styles.fanFace}>
          <BriefThumb brief={art} className={styles.fanArt} />
          <span className={styles.fanTitle}>
            <BriefTitle brief={brief} />
          </span>
        </span>
      )
      return brief.open ? (
        <Link
          key={brief.title}
          to={brief.open.to}
          className={styles.fanCard}
          ref={placeCard(box, i)}
          onMouseEnter={partFor}
          onFocus={partFor}
          tabIndex={stage === 'open' ? 0 : -1}
        >
          {faces}
        </Link>
      ) : (
        <div
          key={brief.title}
          className={`${styles.fanCard} has-tip`}
          data-coming-soon=''
          ref={placeCard(box, i)}
          onMouseEnter={partFor}
        >
          <span className='tip center'>Coming soon</span>
          {faces}
        </div>
      )
    })}
  </div>
)

/* a hovered (or focused) card tells the hand how far the cards after it slide along */
const partFor = (event: React.SyntheticEvent<HTMLElement>) => {
  const card = event.currentTarget
  card.parentElement?.style.setProperty('--part', card.style.getPropertyValue('--fpart'))
}

/* DEMO ONLY: the cards fan out when the wallet opens, and fold back when it closes, before they're removed */
const useFan = (walletRef: React.RefObject<HTMLDivElement>, open: boolean, ready: boolean[]) => {
  const n = ready.length
  const [fan, setFan] = useState<{ stage: FanStage; from: Map<string, CardBox>; to: CardBox[] }>()
  useLayoutEffect(() => {
    const wallet = walletRef.current
    if (!wallet) return undefined
    if (open) {
      setFan({ stage: 'from', from: stackBoxes(wallet), to: fanBoxes(wallet, ready) })
      // a frame later, once the cards stand where the stacked ones do, they fan out
      const frame = window.requestAnimationFrame(() =>
        window.requestAnimationFrame(() => setFan(current => current && { ...current, stage: 'open' }))
      )
      return () => window.cancelAnimationFrame(frame)
    }
    // closing: the cards go back to their places in the stack, then fade as the stack fades back in under them
    setFan(current => current && { ...current, stage: 'closing', from: stackBoxes(wallet) })
    const landed = window.setTimeout(
      () => setFan(current => current && { ...current, stage: 'landing' }),
      FAN_CLOSE_MS + n * 20
    )
    const done = window.setTimeout(() => setFan(undefined), FAN_CLOSE_MS + n * 20 + 220)
    return () => {
      window.clearTimeout(landed)
      window.clearTimeout(done)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only as the wallet opens and closes
  }, [open])
  return fan
}

const WalletCard: FunctionComponent<{
  wallet: Wallet
  showComingSoon: boolean
  open: boolean
  onToggle: () => void
}> = ({ wallet, showComingSoon, open, onToggle }) => {
  const [opened, setOpened] = useState(false)
  const comingSoon = wallet.briefs.filter(brief => !brief.open).length
  const folded = !showComingSoon && !opened && comingSoon > 0
  const briefs = folded ? wallet.briefs.filter(brief => brief.open) : wallet.briefs
  const walletRef = useRef<HTMLDivElement>(null)
  const fan = useFan(
    walletRef,
    open,
    wallet.briefs.map(brief => Boolean(brief.open))
  )

  return (
    <div
      className={`wallet fp ${open ? styles.walletOpen : ''}`}
      data-wallet={wallet.name}
      data-fanned={fan && fan.stage !== 'landing' ? '' : undefined}
      ref={walletRef}
    >
      <div className='list condensed stacked' data-list='briefings'>
        <ul className='no-margin-bottom'>
          {folded && (
            <li data-title=''>
              <div className='row row-link'>
                <div className='col title'>
                  <button type='button' className={styles.moreComingSoon} onClick={() => setOpened(true)}>
                    +{comingSoon} coming soon
                  </button>
                </div>
              </div>
            </li>
          )}
          {briefs.map(brief =>
            brief.open ? (
              <li key={brief.title} data-title={brief.title} data-highlight=''>
                <Link to={brief.open.to} className='row row-link' data-brief={brief.open.brief}>
                  <BriefThumb brief={brief.open.thumb} className={styles.briefWatermark} />
                  <div className='col title'>
                    <div className='ellipsis'>
                      <BriefTitle brief={brief} />
                    </div>
                  </div>
                </Link>
              </li>
            ) : (
              <li key={brief.title} data-title={brief.title}>
                <div className='row row-link has-tip'>
                  <span className='tip center'>Coming soon</span>
                  <div className='col title'>
                    <div className='ellipsis'>{brief.title}</div>
                  </div>
                </div>
              </li>
            )
          )}
        </ul>
      </div>
      {fan && <FanCards wallet={wallet} stage={fan.stage} from={fan.from} to={fan.to} />}
      <span className='leather'>
        <h5>{wallet.name}</h5>
        <button
          type='button'
          className={styles.walletToggle}
          aria-expanded={open}
          aria-label={`All ${wallet.briefs.length} briefs in ${wallet.name}`}
          onClick={onToggle}
        />
      </span>
    </div>
  )
}

/* each brief once (the board repeats some in several wallets), with the stages and jobs it's for: the ones to
   open first, then the coming-soon ones, each in lifecycle order */
interface BriefRow {
  brief: BoardBrief
  columns: BoardColumn[]
  jobs: string[]
}

const briefRows = (wallets: Wallet[]): BriefRow[] => {
  const rows = new Map<string, BriefRow>()
  wallets.forEach(wallet =>
    wallet.briefs.forEach(brief => {
      const row = rows.get(brief.title) ?? { brief, columns: [], jobs: [] }
      if (!row.columns.includes(wallet.column)) row.columns.push(wallet.column)
      if (!row.jobs.includes(wallet.name)) row.jobs.push(wallet.name)
      row.brief = { ...row.brief, isNew: row.brief.isNew || brief.isNew }
      rows.set(brief.title, row)
    })
  )
  const all = Array.from(rows.values())
  return [...all.filter(row => row.brief.open), ...all.filter(row => !row.brief.open)]
}

const columnLabel = (column: BoardColumn) => BOARD_COLUMNS.find(c => c.value === column)?.label ?? column

/* a lifecycle column's name beside a dot in its colour, as in the customers table */
const StageName: FunctionComponent<{ column: BoardColumn; current: boolean }> = ({ column, current }) => (
  <span className={styles.stageName} data-column={column} data-current={current || undefined}>
    {columnLabel(column)}
  </span>
)

/* DEMO ONLY: the briefs as a list, styled as the customers table: a row per brief (the ready ones open from
   anywhere on the row), its stages (the customer's current one in bold) and jobs, and its status. Coming-soon
   briefs can be folded away behind a button, as on the board */
const BriefsTable: FunctionComponent<{
  wallets: Wallet[]
  current: BoardColumn[]
  currentLabel: string
  showComingSoon: boolean
}> = ({ wallets, current, currentLabel, showComingSoon }) => {
  const [opened, setOpened] = useState(false)
  const navigate = useNavigate()
  const rows = briefRows(wallets)
  const comingSoon = rows.filter(row => !row.brief.open).length
  const shown = showComingSoon || opened ? rows : rows.filter(row => row.brief.open)
  const firstCurrentRow = shown.findIndex(row => row.columns.some(column => current.includes(column)))

  return (
    <>
      <div className={`${tableStyles.customerTableContainer} ${styles.listTable}`}>
        <table className={tableStyles.customerTable} aria-label='Briefs'>
          <thead>
            <tr>
              <th>Brief</th>
              <th>Lifecycle stage</th>
              <th>Used for</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {shown.map(({ brief, columns: briefColumns, jobs }, i) => {
              const { open } = brief
              // the customer's current stage is flagged once, beside it in the first row it's in
              const flagged = i === firstCurrentRow ? briefColumns.find(column => current.includes(column)) : undefined
              return (
                <tr
                  key={brief.title}
                  ref={entranceOrder(i)}
                  className={`${styles.listRow} ${open ? tableStyles.customerTableRow : styles.listComingSoonRow}`}
                  onClick={open ? () => navigate(open.to) : undefined}
                >
                  <td className={styles.listBriefCell}>
                    {/* the brief's artwork in an angled panel down the row's left edge, as the Intel tab's section
                        headers have their icons (a coming-soon brief's in grey, as on the board) */}
                    <span className={styles.listArt} data-coming-soon={!open || undefined} aria-hidden='true'>
                      <BriefThumb brief={open ? open.thumb : 'health-check'} className={styles.listArtImage} />
                    </span>
                    {open ? (
                      <Link
                        to={open.to}
                        className={tableStyles.customerNameLink}
                        onClick={event => event.stopPropagation()}
                      >
                        <BriefTitle brief={brief} />
                      </Link>
                    ) : (
                      brief.title
                    )}
                  </td>
                  <td>
                    <span className={styles.stageNames}>
                      {briefColumns.map(column => (
                        <span key={column} className={styles.stageNameWithChip}>
                          <StageName column={column} current={current.includes(column)} />
                          {column === flagged && (
                            <span className={styles.currentChip} title={`Current stage: ${currentLabel}`}>
                              Current
                            </span>
                          )}
                        </span>
                      ))}
                    </span>
                  </td>
                  <td>{jobs.join(', ')}</td>
                  <td className={styles.listStatus}>
                    {open ? 'Ready' : <span className={styles.comingSoon}>Coming soon</span>}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      {!showComingSoon && !opened && comingSoon > 0 && (
        <button type='button' className={`btn secondary sml ${styles.listMore}`} onClick={() => setOpened(true)}>
          Show {comingSoon} coming soon
        </button>
      )}
    </>
  )
}

/* DEMO ONLY: the briefs as books on shelves, a shelf per lifecycle stage, its books grouped by job (as the
   board's wallets group them, each group labelled on the shelf under it). The ones ready to open stand face out:
   covers in the stage's colour, with the brief's artwork as a watermark and its title at the foot,
   lifting when hovered. The coming-soon ones stand beside them spine out, their titles running up the spine; with
   coming-soon briefs folded away, a group's spines become one, "+N" */
/* how the books vary as they stand: a few heights and widths for the spines, in turn, and the last spine of a group
   leaning against the air beside it */
const SPINE_VARIANTS = 5

/* and the covers stand at a few heights, as real books do */
const BOOK_HEIGHTS = 3

const BookGroup: FunctionComponent<{
  wallet: Wallet
  showComingSoon: boolean
  first: number
  /** how many covers, and how many spines, stand on the shelf before this group's */
  shade: { covers: number; spines: number }
}> = ({ wallet, showComingSoon, first, shade }) => {
  const [opened, setOpened] = useState(false)
  const ready = wallet.briefs.filter(brief => brief.open)
  const comingSoon = wallet.briefs.filter(brief => !brief.open)
  const folded = !showComingSoon && !opened && comingSoon.length > 0
  const spines = folded ? 1 : comingSoon.length
  // a lean only with company: a group of one stands up straight
  const leans = (spine: number) => ready.length + spines > 1 && spine === spines - 1
  return (
    <li className={styles.bookGroup}>
      <ul className={styles.books} aria-label={wallet.name}>
        {ready.map((brief, i) => {
          const open = brief.open as NonNullable<BoardBrief['open']>
          const Art = BOOK_ART[open.thumb]
          return (
            <li key={brief.title} ref={entranceOrder(first + i)} className={styles.bookSlot} data-cover>
              <Link
                to={open.to}
                className={styles.book}
                data-column={wallet.column}
                data-shade={(shade.covers + i) % COVER_SHADES}
                data-height={(first + i) % BOOK_HEIGHTS}
                data-brief={open.brief}
              >
                <Art className={styles.bookWatermark} preserveAspectRatio='xMidYMin slice' aria-hidden='true' />
                {brief.isNew && (
                  <span className={styles.bookNew}>
                    <span className={styles.newBadge}>New</span>
                  </span>
                )}
                <span className={styles.bookTitle}>{brief.title}</span>
              </Link>
            </li>
          )
        })}
        {folded ? (
          <li ref={entranceOrder(first + ready.length)} className={styles.bookSlot} data-lean={leans(0) || undefined}>
            <button
              type='button'
              className={styles.spine}
              data-variant={0}
              data-column={wallet.column}
              data-shade={(shade.spines + 1) % COVER_SHADES}
              onClick={() => setOpened(true)}
            >
              <span>+{comingSoon.length} coming soon</span>
            </button>
          </li>
        ) : (
          comingSoon.map((brief, i) => (
            <li
              key={brief.title}
              ref={entranceOrder(first + ready.length + i)}
              className={styles.bookSlot}
              data-lean={leans(i) || undefined}
            >
              <span
                className={`${styles.spine} has-tip`}
                data-variant={(first + i) % SPINE_VARIANTS}
                data-column={wallet.column}
                data-shade={(shade.spines + i + 1) % COVER_SHADES}
              >
                <span className='tip center'>Coming soon</span>
                <span>{brief.title}</span>
              </span>
            </li>
          ))
        )}
      </ul>
      <h5 className={styles.bookGroupName}>{wallet.name}</h5>
    </li>
  )
}

const Shelf: FunctionComponent<{
  column: BoardColumn
  wallets: Wallet[]
  current: boolean
  showComingSoon: boolean
  /** where its books come in, after the shelves before it */
  first: number
}> = ({ column, wallets, current, showComingSoon, first }) => {
  const groups = wallets.filter(wallet => wallet.column === column)
  if (groups.length === 0) return null
  // each group's books come in after the group before it's
  const starts = groups.reduce<number[]>(
    (list, wallet, i) => [...list, i === 0 ? first : list[i - 1] + groups[i - 1].briefs.length],
    []
  )
  const before = (i: number, coming: boolean) =>
    groups.slice(0, i).reduce((n, wallet) => n + wallet.briefs.filter(brief => !brief.open === coming).length, 0)
  return (
    <section className={styles.shelf} data-column={column} aria-label={`${columnLabel(column)} briefs`}>
      <h4 className={styles.shelfTitle}>
        <StageName column={column} current={current} />
        {current && <span className={styles.currentChip}>Current stage</span>}
      </h4>
      <ul className={styles.bookGroups}>
        {groups.map((wallet, i) => (
          <BookGroup
            key={wallet.name}
            wallet={wallet}
            showComingSoon={showComingSoon}
            first={starts[i]}
            shade={{ covers: before(i, false), spines: before(i, true) }}
          />
        ))}
      </ul>
    </section>
  )
}

/* the customer's lifecycle stage, as set on the Intel tab (undefined when it isn't set), and whether it has loaded */
const useLifecycleStage = (teamID: string, customerID: string): { stage?: LifecycleStage; loaded: boolean } => {
  const [stage, setStage] = useState<LifecycleStage>()
  const [loaded, setLoaded] = useState(false)
  useEffect(() => {
    setLoaded(false)
    const controller = new AbortController()
    const request = axios.get<{ lifecycleStage?: string }>(`/api/teams/${teamID}/customers/${customerID}/profile`, {
      signal: controller.signal,
    })
    if (request && typeof request.then === 'function') {
      request
        .then(response => {
          const value = response?.data?.lifecycleStage
          setStage(isLifecycleStage(value) ? value : undefined)
          setLoaded(true)
        })
        .catch(err => {
          if (!axios.isCancel(err)) setLoaded(true) // no stage: the board shows its default
        })
    } else {
      setLoaded(true)
    }
    return () => controller.abort()
  }, [teamID, customerID])
  return { stage, loaded }
}

/* each layout's heading and introduction */
const INTROS: Record<'board' | 'list' | 'shelf', { title: string; description: string }> = {
  board: {
    title: 'Briefs by lifecycle',
    description: "Curated, point-in-time views of the customer's PaperCut deployment across their lifecycle.",
  },
  list: {
    title: 'All briefs',
    description: 'Every brief for this customer, with the lifecycle stages and jobs it is for.',
  },
  shelf: {
    title: 'Brief library',
    description: "The briefs for each stage of the customer's lifecycle, shelved by the job they do.",
  },
}

const BriefsList: FunctionComponent<{ teamID: string; customerID: string }> = ({ teamID, customerID }) => {
  // the stage set on the Intel tab is the current stage (Serve & upsell until one is set); it can span columns
  const lifecycle = useLifecycleStage(teamID, customerID)
  const stage = stageOrDefault(lifecycle.stage)
  const current: BoardColumn[] = lifecycleStageFor(stage)?.columns ?? []
  // DEMO ONLY: the list and shelves don't wait for the stage: they show straight away, and flag the current stage
  // once it has arrived (rather than flagging the default and then moving the flag)
  const flagged: BoardColumn[] = lifecycle.loaded ? current : []
  const first = current[0]
  // the Expired column only appears for an expired customer
  const expired = stage === 'expired'
  const columns = BOARD_COLUMNS.filter(column => expired || column.value !== 'expired')
  const walletsShown = WALLETS.filter(wallet => columns.some(column => column.value === wallet.column))
  // DEMO ONLY: the layout (lifecycle board, list or shelf), and whether coming-soon briefs show
  const { options } = useDemoOptions()
  const layout = options.briefsView

  // DEMO ONLY: the wallet opened to show all its briefs (one at a time), closed by Esc or a click outside it
  const [openWallet, setOpenWallet] = useState<string>()
  useEffect(() => {
    if (!openWallet) return undefined
    const close = (event: MouseEvent | KeyboardEvent) => {
      if (event instanceof KeyboardEvent ? event.key === 'Escape' : !(event.target as Element).closest?.('.wallet')) {
        setOpenWallet(undefined)
      }
    }
    document.addEventListener('mousedown', close)
    document.addEventListener('keydown', close)
    return () => {
      document.removeEventListener('mousedown', close)
      document.removeEventListener('keydown', close)
    }
  }, [openWallet])

  // On narrow screens the lifecycle scrolls sideways; start it at the current stage rather than Trial.
  const processRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const strip = processRef.current
    if (!strip || strip.scrollWidth <= strip.clientWidth) return
    const col = strip.querySelector(`header[data-content="${first}"]`)?.closest('.col')
    if (!col) return
    strip.scrollLeft = col.getBoundingClientRect().left - strip.getBoundingClientRect().left - 8
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-run when the stage arrives
  }, [first, lifecycle.loaded])

  return (
    <div>
      <header className={styles.header}>
        <h2>
          {INTROS[layout].title} <span className={styles.comingSoon}>More briefs coming soon</span>
        </h2>
      </header>
      <p>{INTROS[layout].description}</p>

      {/* DEMO ONLY: the briefs as a list, its current stage in bold */}
      {layout === 'list' && (
        <BriefsTable
          wallets={walletsShown}
          current={flagged}
          currentLabel={lifecycleStageFor(stage)?.label ?? ''}
          showComingSoon={options.showComingSoonBriefs}
        />
      )}

      {/* DEMO ONLY: the briefs on shelves, a shelf per stage */}
      {layout === 'shelf' && (
        <div className={styles.shelves}>
          {columns.map(({ value }, i) => (
            <Shelf
              key={value}
              column={value}
              wallets={walletsShown}
              current={flagged.includes(value)}
              showComingSoon={options.showComingSoonBriefs}
              first={i * 3}
            />
          ))}
        </div>
      )}

      {/* the board waits for the stage, so it arrives with the right columns and Current stage (no late changes) */}
      {lifecycle.loaded && layout === 'board' && (
        <div data-group='process' ref={processRef}>
          {/* Stage Headers Row: the current stage in colour, flagged "Current stage" */}
          <div className='row flex'>
            {columns.map(({ value, label }) => {
              const isCurrent = current.includes(value)
              // the flag spans every column of the current stage (Serve & upsell has two)
              const span = current.length === 1 ? styles.currentStageSingle : ''
              return (
                <div key={value} className={isCurrent ? 'col relative' : 'col'}>
                  {value === first && (
                    <div className={`tip visible center fp ${span}`} data-testid='current-stage'>
                      <strong>Current stage</strong>
                    </div>
                  )}
                  <header data-content={value} data-highlight={isCurrent ? '' : undefined}>
                    {label}
                  </header>
                </div>
              )
            })}
          </div>

          {/* Content & Grid Overlay Area */}
          <div className='relative'>
            <div className='row flex' data-content='grid-overlay'>
              {columns.map(({ value }) => (
                <div key={value} className='col'></div>
              ))}
            </div>

            <div className='row flex'>
              {columns.map(({ value }) => (
                <div key={value} className='col' data-content={value}>
                  {walletsShown
                    .filter(wallet => wallet.column === value)
                    .map(wallet => (
                      <WalletCard
                        key={wallet.name}
                        wallet={wallet}
                        showComingSoon={options.showComingSoonBriefs}
                        open={openWallet === wallet.name}
                        onToggle={() => setOpenWallet(openWallet === wallet.name ? undefined : wallet.name)}
                      />
                    ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export const BriefsHub: FunctionComponent<Props> = ({ teamID, host }) => (
  <Routes>
    <Route index element={<BriefsList teamID={teamID} customerID={host.id} />} />
    <Route path='health-check' element={<HealthCheckBrief teamID={teamID} host={host} />} />
    <Route path='fleet-makeup' element={<FleetMakeupBrief teamID={teamID} host={host} />} />
    <Route path='security' element={<SecurityBrief teamID={teamID} host={host} />} />
    {/* DEMO ONLY */}
    <Route path='security-demo' element={<SecurityDemoBrief teamID={teamID} host={host} />} />
    <Route path='*' element={<Navigate to='.' replace />} />
  </Routes>
)
